import { useSignal } from "@preact/signals";

export const MAX_FILE_SIZE = 50 * 1024 * 1024;

export async function fileToString(file: File | Blob): Promise<string> {
  if (!file) throw new Error('No file provided');

  if ((file as File).size && (file as File).size > MAX_FILE_SIZE) {
    throw new Error('File too large. Max size is 50MB.');
  }

  const textTypes = ['txt', 'md', 'markdown', 'csv', 'json', 'xml', 'html', 'htm'];
  const name = (file as File).name || '';
  const ext = name.toLowerCase().substring(name.lastIndexOf('.') + 1);

  let text = '';

  if (textTypes.includes(ext)) {
    text = await (file as Blob).text();
  } else {
    const DocToTextCtor = (globalThis as unknown as { DocToText?: { new(): { extractToText: (f: File, ext: string) => Promise<string> } } }).DocToText;
    if (!DocToTextCtor) throw new Error('DocToText library not available');
    const docToText = new DocToTextCtor();
    if (typeof docToText.extractToText !== 'function') throw new Error('DocToText.extractToText not available');
    text = await docToText.extractToText(file as File, ext);
  }

  if (text.length > 100000) throw new Error('File is too big.');

  return text;
}

export default fileToString;


const imageTypes = ['jpg', 'jpeg', 'png', 'gif'];
const MAX_IMAGE_DIMENSION = 2048;

function fileExt(name: string) {
  return name.toLowerCase().substring(name.lastIndexOf('.') + 1);
}

async function downscaleImage(file: File): Promise<Blob> {
  if (file.type == 'image/gif') return file; // Canvas would lose animation

  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(bitmap.width, bitmap.height));
  if (scale == 1) return file;

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

  const type = file.type == 'image/png' ? 'image/png' : 'image/jpeg';
  return await new Promise((resolve, reject) =>
    canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Error resizing image')), type, 0.9));
}

export async function uploadImage(file: File): Promise<string> {
  const blob = await downscaleImage(file);

  const formData = new FormData();
  formData.append('file', blob, file.name);

  const res = await fetch('/api/upload-image', { method: 'POST', body: formData });
  if (!res.ok) throw new Error('Error uploading image');

  const { filePath } = await res.json() as { filePath: string };
  return filePath;
}

export interface FileData {
  id: number;
  name: string;
  text: string;
  filePath?: string;
  previewUrl?: string;
}

export function useFileHandler() {
  const files = useSignal<FileData[]>([]);
  const isDragging = useSignal(false);

  async function addFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;

    const newFilesData: FileData[] = [];
    const existingFileNames = new Set(files.value.map(f => f.name));

    for (const file of Array.from(fileList)) {
      if (file.size > MAX_FILE_SIZE) {
        alert(`File "${file.name}" is too large. Max size is 50MB.`);
        continue;
      }

      if (existingFileNames.has(file.name)) {
        console.log(`File "${file.name}" is already added.`);
        continue;
      }

      try {
        if (imageTypes.includes(fileExt(file.name))) {
          const filePath = await uploadImage(file);
          newFilesData.push({
            id: Math.random(),
            name: file.name,
            text: '',
            filePath,
            previewUrl: URL.createObjectURL(file),
          });
        } else {
          const text = await fileToString(file);
          newFilesData.push({
            id: Math.random(),
            name: file.name,
            text,
          });
        }
        existingFileNames.add(file.name);
      } catch (error) {
        alert(`Error processing file: "${error + ''}`);
      }
    }

    if (newFilesData.length > 0) {
      files.value = [...files.value, ...newFilesData];
    }
  }

  function uploadFile() {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    const textTypes = '.txt,.md,.markdown,.csv,.json,.xml,.html,.htm';
    const imageExts = '.jpg,.jpeg,.png,.gif,';
    input.accept = '.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,.hwp,' + imageExts + textTypes;
    input.onchange = async (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files) {
        await addFiles(target.files);
      }
    };
    input.click();
  }

  function handleDragOver(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDragEnter(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    isDragging.value = true;
  }

  function handleDragLeave(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (e.relatedTarget && (e.currentTarget as Node).contains(e.relatedTarget as Node)) {
      return;
    }
    isDragging.value = false;
  }

  async function handleDrop(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    isDragging.value = false;
    if (e.dataTransfer?.files) {
      await addFiles(e.dataTransfer.files);
    }
  }

  function DragBox() {
    if (!isDragging.value) return <></>;
    return <div style={
      {
        position: 'fixed', top: '1rem', right: '1rem', bottom: '1rem', left: '1rem',
        backgroundColor: 'rgba(0, 100, 255, 0.1)', border: '2px dashed #0066ff',
        borderRadius: '12px', display: 'flex', justifyContent: 'center',
        alignItems: 'center', fontSize: '2rem', color: '#0066ff',
        pointerEvents: 'none', zIndex: 9999,
      }
    }>
      <span>Drop files to add them </span>
    </div>
  }

  const getDragHandlers = () => ({
    onDragEnter: handleDragEnter,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  });

  return {
    files,
    isDragging,
    DragBox,
    uploadFile,
    getDragHandlers,
  };
}